/* ============================================================
   PARCU — size only quick add on the product card

   The panel behind the plus is the theme's own quick buy fragment:
   a variant picker of three selects, Fit, Size and Colour, and an
   add to cart form. This reduces it to the one decision worth
   making on a card.

   On a phone it is a sheet across the bottom of the window. On a
   laptop it is a strip across the bottom of the card itself: a
   sheet that covers the whole window to ask for one size is a
   phone pattern and reads as a mistake on a large screen.

   Two things about that fragment matter.

   The sheet is moved onto <body> while it is open. position:fixed
   is only relative to the window when no ancestor has a transform,
   and the theme puts one on every section for its reveal, so left
   inside the card the sheet anchored itself to the grid and landed
   halfway down the page. It goes back on close, so the theme's own
   markup is never left rearranged. The move waits for the fragment
   to load, because taking the element out mid fetch cancelled it.
   The inline strip has no such problem and stays in the card.

   And the selects in that fragment are inert here. The script that
   makes a variant picker update the hidden variant id ships with
   the product page, not the collection page, so changing a select
   left the form still pointing at the variant the card opened on.
   Driving the picker was therefore never going to work: the size
   is resolved against the product's own variant list from
   /products/<handle>.js and written straight to the hidden input,
   then the theme's own add to cart button is clicked. That list is
   also what decides which sizes are sold out, which is per variant
   and so more accurate than a disabled option would be.
   ============================================================ */

(function () {
    'use strict';

    var SIZE = /^(xxs|xs|s|m|l|xl|xxl|2xl|3xl|4xl)$/i;
    var cache = {};

    function productUrl(menu) {
        var el = menu.querySelector('[data-product-url]');
        return el ? el.getAttribute('data-product-url') : null;
    }

    function load(url) {
        if (cache[url]) return cache[url];
        cache[url] = fetch(url + '.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        return cache[url];
    }

    function idField(menu) {
        return menu.querySelector('input[name="id"], .variant-id-input');
    }

    /* The option whose values are all sizes. Found by content, not
       by name, because the option is called Size in English and
       something else in every market the store sells to. */
    function sizeIndex(product) {
        for (var i = 0; i < product.options.length; i++) {
            var vals = product.options[i].values || product.options[i], ok = vals.length > 0, j;
            for (j = 0; j < vals.length; j++) {
                if (!SIZE.test(String(vals[j]).trim())) { ok = false; break; }
            }
            if (ok) return i;
        }
        return -1;
    }

    function variantById(product, id) {
        for (var i = 0; i < product.variants.length; i++) {
            if (String(product.variants[i].id) === String(id)) return product.variants[i];
        }
        return null;
    }

    /* The same variant as the one the card is on, with one option
       swapped. Everything else stays, which is what keeps the
       shopper on the colour and fit they were already looking at. */
    function variantFor(product, current, idx, value) {
        for (var i = 0; i < product.variants.length; i++) {
            var v = product.variants[i], ok = true;
            for (var j = 0; j < current.options.length; j++) {
                var want = j === idx ? value : current.options[j];
                if (v.options[j] !== want) { ok = false; break; }
            }
            if (ok) return v;
        }
        return null;
    }

    /* Inline on anything laptop sized, and the window is what is
       measured.

       This used to budget 46px per size against the width of the
       card, which sounds safer and was wrong in practice: a five
       across grid lands the card at 251px at 1280, 283 at 1440 and
       297 at 1512, against a threshold of 300 for a six size run.
       Every common laptop width fell under it, a maximised 1512
       window by three pixels, so the phone sheet was what a laptop
       always got. The strip wraps to a second row now, so a narrow
       card is no longer a reason to fall back to covering the whole
       window. 778 is the breakpoint the inline rules are written
       under in the stylesheet and the two have to agree. */
    function inlineFits(media) {
        return !!media && window.matchMedia('(min-width: 778px)').matches;
    }

    function detach(menu, details) {
        if (menu.parcuOut) return;
        var card = details.closest('product-card');
        var media = card && card.querySelector('.media');
        var wide = inlineFits(media);
        menu.parcuOut = true;
        menu.parcuHome = menu.parentNode;
        menu.parcuDetails = details;
        details.parcuMenu = menu;
        menu.classList.add('parcu-sheet');
        if (wide) { menu.classList.add('parcu-inline'); media.appendChild(menu); return; }
        document.body.appendChild(menu);
        document.documentElement.classList.add('parcu-sheet-open');
    }

    function maybeDetach(menu) {
        var d = menu.parcuDetails || menu.closest('details');
        if (!d || !d.open) return;
        if (!menu.querySelector('.parcu-size')) return;
        detach(menu, d);
    }

    function reattach(menu) {
        menu.classList.remove('parcu-sheet', 'parcu-inline', 'is-busy');
        menu.parcuOut = false;
        if (menu.parcuHome && menu.parentNode !== menu.parcuHome) {
            menu.parcuHome.appendChild(menu);
        }
        if (!document.querySelector('.parcu-qadd details[open]')) {
            document.documentElement.classList.remove('parcu-sheet-open');
        }
    }

    function close(menu) {
        var d = menu.parcuDetails || menu.closest('details');
        if (d) { d.removeAttribute('open'); } else { reattach(menu); }
    }

    function render(menu, product) {
        var field = idField(menu);
        if (!field) return;
        var current = variantById(product, field.value);
        if (!current) return;
        var idx = sizeIndex(product);
        if (idx < 0) return;

        var values = product.options[idx].values || product.options[idx];
        var stamp = String(current.id) + ':' + values.join(',');

        var host = menu.querySelector('.parcu-sizes');
        if (!host) {
            host = document.createElement('div');
            host.className = 'parcu-sizes';
            var add = menu.querySelector('button[name="add"]');
            if (add && add.parentNode) {
                add.parentNode.insertBefore(host, add);
            } else {
                (menu.querySelector('form') || menu).appendChild(host);
            }
        }
        if (host.dataset.stamp !== stamp) {
            host.dataset.stamp = stamp;
            host.innerHTML = '';
            for (var i = 0; i < values.length; i++) {
                var v = variantFor(product, current, idx, values[i]);
                var out = !v || !v.available;
                var b = document.createElement('button');
                b.type = 'button';
                b.className = 'parcu-size' + (out ? ' is-out' : '');
                b.textContent = String(values[i]).toUpperCase();
                b.setAttribute('data-value', values[i]);
                if (v) b.setAttribute('data-variant', v.id);
                if (out) b.disabled = true;
                host.appendChild(b);
            }
        }

        /* Only now are the theme's own controls taken out of the
           sheet. If anything above failed, its picker is still
           there and still usable. */
        var sets = menu.querySelectorAll('fieldset');
        for (var k = 0; k < sets.length; k++) sets[k].classList.add('parcu-hidefield');
        if (!menu.querySelector('.parcu-qclose')) {
            var x = document.createElement('button');
            x.type = 'button';
            x.className = 'parcu-qclose';
            x.setAttribute('aria-label', 'Close');
            menu.insertBefore(x, menu.firstChild);
        }
        menu.classList.add('parcu-ready');
        maybeDetach(menu);
    }

    function build(menu) {
        if (!idField(menu)) return;                 /* fragment not in yet */
        var url = productUrl(menu);
        if (!url) return;
        if (menu.parcuPending === url) return;
        menu.parcuPending = url;
        load(url).then(function (product) {
            menu.parcuPending = null;
            if (product && product.variants) render(menu, product);
        });
    }

    function scan() {
        var menus = document.querySelectorAll('details-menu.quickBuy');
        for (var i = 0; i < menus.length; i++) build(menus[i]);
    }

    /* toggle does not bubble, but the capture phase still reaches
       an ancestor, so one listener covers every card on the page. */
    document.addEventListener('toggle', function (e) {
        var d = e.target;
        if (!d || d.tagName !== 'DETAILS' || !d.closest || !d.closest('.parcu-qadd')) return;
        var menu = d.parcuMenu || d.querySelector('details-menu.quickBuy');
        if (!menu) return;
        if (d.open) {
            var others = document.querySelectorAll('.parcu-qadd details[open]'), i;
            for (i = 0; i < others.length; i++) {
                if (others[i] !== d) others[i].removeAttribute('open');
            }
            build(menu);
        } else {
            reattach(menu);
        }
    }, true);

    document.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;

        /* The sheet covers the window, so a tap on the dimmed area
           lands on the sheet element itself. That is the close. The
           inline strip covers nothing, so it has no dimmed area and
           is closed by the handler below instead. */
        if (t.classList && t.classList.contains('parcu-sheet') && !t.classList.contains('parcu-inline')) {
            close(t);
            return;
        }

        if (t.closest('.parcu-qclose')) {
            e.preventDefault();
            close(t.closest('details-menu'));
            return;
        }

        var btn = t.closest('.parcu-size');
        if (!btn || btn.disabled) return;
        e.preventDefault();
        e.stopPropagation();

        var menu = btn.closest('details-menu');
        var variant = btn.getAttribute('data-variant');
        var field = menu && idField(menu);
        if (!menu || !variant || !field || menu.classList.contains('is-busy')) return;

        var host = btn.parentNode, i;
        for (i = 0; i < host.children.length; i++) host.children[i].classList.remove('is-on');
        btn.classList.add('is-on');
        menu.classList.add('is-busy');

        field.value = variant;
        var add = menu.querySelector('button[name="add"]');
        if (add) add.click();

        window.setTimeout(function () {
            menu.classList.remove('is-busy');
            close(menu);
        }, 600);
    }, true);

    /* Clicking away closes the inline strip. The phone sheet gets
       this for free from its backdrop; the strip sits on the card
       and would otherwise stay open until the x was found, which on
       a grid of cards is the sort of thing that is still open three
       products later. Bubble phase on purpose, so it runs after the
       handler above and never sees a size click, which stops its
       own propagation. */
    document.addEventListener('click', function (e) {
        var open = document.querySelector('.parcu-qadd details[open]');
        if (!open) return;
        var menu = open.parcuMenu;
        if (!menu || !menu.classList.contains('parcu-inline')) return;
        var t = e.target;
        if (!t || !t.closest) return;
        if (menu.contains(t)) return;
        if (t.closest('.parcu-qadd') === open.closest('.parcu-qadd')) return;
        open.removeAttribute('open');
    });

    document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var open = document.querySelector('.parcu-qadd details[open]');
        if (open) open.removeAttribute('open');
    });

    function start() {
        scan();
        if (!window.MutationObserver) return;
        var timer;
        new MutationObserver(function () {
            clearTimeout(timer);
            timer = window.setTimeout(scan, 80);
        }).observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
